import React, { useState, useMemo } from 'react';
import { AssignedMicrophone } from 'core/models/layout';
import { IconButton, SxProps, Theme } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { MicrophoneList } from 'components/views/atoms/MicrophoneList';
import { MicrophoneDevice, MediaDevice } from 'core/models/mediaDevice';
import { AudioStreamConfigForm } from '../AudioStreamConfigForm';
import { SafeBox } from 'components/views/atoms/SafeBox';

const styles = {
    frame: {
        margin: theme => theme.spacing(1),
        padding: theme => theme.spacing(1),
        border: 'thin solid',
        borderRadius: theme => theme.shape.borderRadius,
        borderColor: 'transparent',
        '&:hover': {
            borderColor: theme => theme.palette.divider,
        },
    } as SxProps<Theme>,
    select: {
        width: '50ex',
    } as SxProps<Theme>,
    clearIndicator: {
        color: theme => theme.palette.action.active,
        visibility: 'visible',
    } as SxProps<Theme>,
    microphoneOptions: {
        marginLeft: theme => theme.spacing(2),
    } as SxProps<Theme>,
    horizontal: {
        display: 'inline-flex',
        flexDirection: 'row',
        alignItems: 'center',
    } as SxProps<Theme>,
    buttonBox: {
        margin: theme => theme.spacing(2),
    } as SxProps<Theme>,
    labeledSelect: {
        marginLeft: '1ex',
    } as SxProps<Theme>,
    duplicateMarker: {
        color: theme => theme.palette.error.main,
        margin: theme => theme.spacing(1),
        fontFamily: theme => theme.typography.caption.fontFamily,
    } as SxProps<Theme>,
    unpluggedMarker: {
        color: theme => theme.palette.error.main,
        margin: theme => theme.spacing(1),
        fontFamily: theme => theme.typography.caption.fontFamily,
    } as SxProps<Theme>,
};

export type MicrophoneSettingFormProps = {
    className?: string;
    assignedMicrophone: AssignedMicrophone;
    duplicate: boolean;
    microphones: MicrophoneDevice[] | undefined;
    isRemovable?: boolean;
    onChange: (assigned: AssignedMicrophone) => void;
    onRemove?: () => void;
    getAudioConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.AudioConfig>>;
    putAudioConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.AudioConfig>) => Promise<void>;
}

export function MicrophoneSettingForm(props: MicrophoneSettingFormProps) {
    const { className, assignedMicrophone, duplicate, microphones, isRemovable, onChange, onRemove, getAudioConfig, putAudioConfig } = props;

    const microphone = useMemo(() => microphones?.find(c => c.tag === assignedMicrophone.tag), [assignedMicrophone.tag, microphones]);
    const unplugged = assignedMicrophone.tag !== undefined && microphone === undefined;

    const [isHovering, setHovering] = useState(false);

    return (
        <SafeBox className={className} sx={styles.frame} onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}>
            <SafeBox sx={styles.horizontal}>
                <SafeBox sx={styles.buttonBox} visibility={(isHovering && isRemovable !== false) ? 'visible' : 'hidden'}>
                    <IconButton onClick={onRemove} size='small' >
                        <Delete />
                    </IconButton>
                </SafeBox>
                <SafeBox>
                    <SafeBox>
                        <SafeBox sx={styles.horizontal}>
                            <MicrophoneList
                                styles={styles}
                                unplugged={unplugged}
                                selected={assignedMicrophone}
                                options={microphones}
                                onChange={assigned => {
                                    const tmp = { ...assignedMicrophone };
                                    delete tmp.tag;
                                    onChange({ ...tmp, ...assigned });
                                }}
                                autoComplete={true}
                            />
                            <SafeBox>
                                {
                                    duplicate == true
                                        ? <SafeBox sx={styles.duplicateMarker}>duplicate</SafeBox>
                                        : <React.Fragment />
                                }
                                {
                                    unplugged == true
                                        ? <SafeBox sx={styles.unpluggedMarker}>unplugged</SafeBox>
                                        : <React.Fragment />
                                }
                            </SafeBox>
                        </SafeBox>
                    </SafeBox>
                    <SafeBox sx={styles.microphoneOptions}>
                        <AudioStreamConfigForm
                            microphone={microphone}
                            getAudioConfig={getAudioConfig}
                            putAudioConfig={putAudioConfig}
                        />
                    </SafeBox>
                </SafeBox>
            </SafeBox >
        </SafeBox >
    );
}
